// The result form of a workshop task (Stage 3a plan §D2, slice 5): the result buttons, the fields
// each result and task takes, quick reasons, the client mirror of the recording rules (plan §E),
// the request body and the submit key, and the mapping of a failed save onto the form. Plain
// module with no Vue, so it runs under `node --test`. The API checks everything again.
// Evidence files (slice 6): at most 3 and 15 MB in total (L18), each checked like a dossier file.
// Stage 6 (waste on, { wasteEnabled: true }): fluids and batteries pick a waste container
// (containerId) instead of typing one. With waste off every output is the same as before.

import { FILE_MAX_BYTES, checkUpload } from "../Acceptance/dossierFiles.mjs";

// Same limits as the API (workshop/rules.js, corrections.REASON_MAX, files.js).
export const REASON_MAX = 500;
export const CONTAINER_MAX = 100;
export const WORKSHOP_BASES = ["weighed", "measured", "estimated"];
export const UPLOAD_MAX_FILES = 3;
export const UPLOAD_MAX_TOTAL_BYTES = FILE_MAX_BYTES;

// The 2×2 result buttons in order. Eccezione needs at least one evidence file.
export const RESULT_BUTTONS = [
  { result: "completed", key: "workshop.result.completed", color: "green" },
  {
    result: "not_present",
    key: "workshop.result.not_present",
    color: "blue-grey",
  },
  {
    result: "not_applicable",
    key: "workshop.result.not_applicable",
    color: "blue-grey",
  },
  {
    result: "exception",
    key: "workshop.result.exception",
    color: "amber-darken-2",
  },
];

const pad = (value) => String(value).padStart(2, "0");

// "YYYY-MM-DDTHH:mm" in the browser's time zone, as <input type="datetime-local"> uses.
export function localDateTime(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

// A fresh form for a task: no result, the task's first unit, performed at now. With waste on it
// also holds the picked containerId.
export function emptyResultForm(
  task,
  now = new Date(),
  { wasteEnabled = false } = {},
) {
  const units = (task && task.quantity && task.quantity.units) || [];
  const performedAt = localDateTime(now);
  const waste = wasteEnabled ? { containerId: null } : {};
  return {
    ...waste,
    result: null,
    quantityValue: "",
    quantityUnit: units[0] || null,
    quantityBasis: null,
    container: "",
    reason: "",
    performedAt,
    // Untouched, performedAt is not sent and the server uses its own clock.
    defaultPerformedAt: performedAt,
    // Evidence files picked for this result (File objects), sent with "Salva".
    files: [],
  };
}

// Whether a result needs at least one evidence file (an exception, or a task asking for a photo).
export function evidenceRequired(task, result) {
  return (
    Boolean(result) && (result === "exception" || task.photoRequired === true)
  );
}

// The upload limit and each file's own check, before anything is sent:
// null | "too_many_files" | "total_size" | "file_too_large" | "unsupported_type".
export function filesProblem(files) {
  const list = files || [];
  if (list.length > UPLOAD_MAX_FILES) return "too_many_files";
  const problem = list.map((file) => checkUpload(file)).find(Boolean);
  if (problem) return problem === "too_large" ? "file_too_large" : problem;
  const total = list.reduce((sum, file) => sum + (file.size || 0), 0);
  return total > UPLOAD_MAX_TOTAL_BYTES ? "total_size" : null;
}

// Which fields the form shows for a task and a result, and which of them are required. With
// waste on (Stage 6, D4) the text container gives way to the container picker: every task with a
// quantity takes one, required for the fluids (containerRequired) when completed.
export function visibleFields(task, result, { wasteEnabled = false } = {}) {
  const quantity =
    Boolean(task && task.quantity) &&
    (result === "completed" || result === "exception");
  if (wasteEnabled) {
    return {
      quantity,
      quantityRequired:
        quantity && result === "completed" && task.quantity.required === true,
      units: quantity ? [...(task.quantity.units || [])] : [],
      container: false,
      containerRequired: false,
      containerPicker: quantity,
      containerPickerRequired:
        quantity && result === "completed" && task.containerRequired === true,
      reason: Boolean(result),
      reasonRequired: Boolean(result) && result !== "completed",
      performedAt: Boolean(result),
    };
  }
  return {
    quantity,
    quantityRequired:
      quantity && result === "completed" && task.quantity.required === true,
    units: quantity ? [...(task.quantity.units || [])] : [],
    container:
      Boolean(task && task.containerRequired) &&
      (result === "completed" || result === "exception"),
    containerRequired:
      Boolean(task && task.containerRequired) && result === "completed",
    reason: Boolean(result),
    reasonRequired: Boolean(result) && result !== "completed",
    performedAt: Boolean(result),
  };
}

// The locale keys of the one-tap reasons for a task and a result ([] when none).
export function quickReasonKeys(task, result) {
  const reasons = task && task.quickReasons;
  return (reasons && result && reasons[result]) || [];
}

// "4,2" or "4.2" -> 4.2; anything else -> NaN.
export function parseQuantity(text) {
  const raw = String(text == null ? "" : text)
    .trim()
    .replace(",", ".");
  return /^-?\d+(\.\d+)?$/.test(raw) ? Number(raw) : NaN;
}

// The performed-at input goes from the arrival day (00:00) to now.
export function performedAtLimits({ arrivalDate, now = new Date() }) {
  return {
    min: arrivalDate ? `${arrivalDate}T00:00` : "",
    max: localDateTime(now),
  };
}

// At most 3 decimals once a quantity goes into a container (API quantity_precision).
const MILLI_DECIMALS = 3;
const tooPrecise = (value) =>
  Math.abs(
    value * 10 ** MILLI_DECIMALS - Math.round(value * 10 ** MILLI_DECIMALS),
  ) > 1e-6;

// { field: code } for what blocks the save; {} when it can be sent. The codes are the API's
// (workshop.form.errors.<code>), plus a few client-only ones. With waste on, container is the
// picked pick-list row ({ id, unit }) for the client unit check.
export function resultFormErrors(
  form,
  task,
  limits,
  { wasteEnabled = false, container = null } = {},
) {
  const errors = {};
  const result = form && form.result;
  if (!result) return { result: "result_required" };
  const fields = visibleFields(task, result, { wasteEnabled });

  const reason = String(form.reason || "").trim();
  if (reason.length > REASON_MAX || (fields.reasonRequired && !reason)) {
    errors.reason = "reason_required";
  }

  if (fields.quantity) {
    const text = String(form.quantityValue || "").trim();
    if (!text) {
      if (fields.quantityRequired) errors.quantityValue = "quantity_required";
    } else {
      const value = parseQuantity(text);
      if (Number.isNaN(value)) errors.quantityValue = "quantity_invalid";
      else if (!(value > 0)) errors.quantityValue = "quantity_not_positive";
      if (!fields.units.includes(form.quantityUnit)) {
        errors.quantityUnit = "quantity_unit_invalid";
      }
      if (!WORKSHOP_BASES.includes(form.quantityBasis)) {
        errors.quantityBasis = "basis_required";
      }
    }
  }

  if (fields.container) {
    const container = String(form.container || "").trim();
    if (container.length > CONTAINER_MAX) {
      errors.container = "container_too_long";
    } else if (fields.containerRequired && !container) {
      errors.container = "container_required";
    }
  }

  if (fields.containerPicker) {
    const quantityText = String(form.quantityValue || "").trim();
    if (!form.containerId) {
      if (fields.containerPickerRequired) {
        errors.containerId = "container_required";
      }
    } else if (container && container.unit !== form.quantityUnit) {
      errors.containerId = "unit_mismatch";
    } else if (!quantityText) {
      errors.quantityValue = "container_needs_quantity";
    } else if (
      !errors.quantityValue &&
      tooPrecise(parseQuantity(quantityText))
    ) {
      errors.quantityValue = "quantity_precision";
    }
  }

  const at = form.performedAt || "";
  if (!at) errors.performedAt = "performed_at_required";
  else if (limits && limits.min && at < limits.min) {
    errors.performedAt = "performed_at_before_arrival";
  } else if (limits && limits.max && at > limits.max) {
    errors.performedAt = "performed_at_future";
  }

  const files = form.files || [];
  const problem = filesProblem(files);
  if (problem) errors.files = problem;
  else if (evidenceRequired(task, result) && !files.length) {
    errors.files = "evidence_required";
  }
  return errors;
}

// The request fields of a valid form: only what the result takes and what was given. A redo
// (slice 7) names the result it replaces: the task's current result when "Ripeti" was pressed.
// With waste on the picked container goes as containerId.
export function resultPayload(
  form,
  task,
  { replacesResultId = null, wasteEnabled = false } = {},
) {
  const fields = visibleFields(task, form.result, { wasteEnabled });
  const payload = { taskCode: task.code, result: form.result };
  const reason = String(form.reason || "").trim();
  if (reason) payload.reason = reason;
  const quantity = String(form.quantityValue || "").trim();
  if (fields.quantity && quantity) {
    payload.quantityValue = String(parseQuantity(quantity));
    payload.quantityUnit = form.quantityUnit;
    payload.quantityBasis = form.quantityBasis;
  }
  const container = String(form.container || "").trim();
  if (fields.container && container) payload.container = container;
  if (fields.containerPicker && form.containerId) {
    payload.containerId = form.containerId;
  }
  if (form.performedAt && form.performedAt !== form.defaultPerformedAt) {
    // datetime-local text has no zone: Date reads it as local time.
    payload.performedAt = new Date(form.performedAt).toISOString();
  }
  if (form.files && form.files.length) payload.files = [...form.files];
  if (replacesResultId) payload.replacesResultId = replacesResultId;
  return payload;
}

const PAYLOAD_FIELDS = [
  "taskCode",
  "result",
  "reason",
  "quantityValue",
  "quantityUnit",
  "quantityBasis",
  "container",
  "performedAt",
  "replacesResultId",
];

// One commandId per result: a retry of the same input reuses it, so a double click records it once.
// The files count by name, size and last change: another file is another result. A redo is never
// the same command as a first save or a redo of another result.
export function resultSubmitKey(payload) {
  const fields = PAYLOAD_FIELDS.map((field) =>
    payload[field] === undefined ? "" : String(payload[field]),
  );
  // Another container is another result (waste on; without one the key is as before).
  if (payload.containerId) fields.push(`container:${payload.containerId}`);
  const files = (payload.files || []).map(
    (file) => `${file.name}:${file.size}:${file.lastModified}`,
  );
  return [...fields, files.join(",")].join("|");
}

// The form fields an API error can point at (details.field).
const FORM_FIELDS = [
  "result",
  "reason",
  "quantityValue",
  "quantityUnit",
  "quantityBasis",
  "container",
  "containerId",
  "performedAt",
  "files",
];

// invalid_input on a field -> the form's code for it.
const INVALID_INPUT_CODES = {
  result: "result_required",
  quantityValue: "quantity_invalid",
  container: "container_too_long",
  performedAt: "performed_at_invalid",
};

const KNOWN_CODES = [
  "unknown_task",
  "reason_required",
  "evidence_required",
  "quantity_required",
  "quantity_unit_invalid",
  "quantity_not_positive",
  "basis_required",
  "quantity_not_allowed",
  "container_required",
  "performed_at_future",
  "performed_at_before_arrival",
  // Stage 6, waste on
  "container_not_found",
  "container_inactive",
  "container_type_not_allowed",
  "unit_mismatch",
  "container_needs_quantity",
  "container_not_allowed",
  "quantity_precision",
];

// details.reason of 413 upload_too_large.
const UPLOAD_REASONS = ["too_many_files", "total_size"];

// A failed save that is not a 409 -> { field, code, text }: field is the form field to show it
// under (null: above "Salva"), code the workshop.form.errors key (null: show text, the API's
// errorMsg).
export function resultServerError(err) {
  const res = err && err.response;
  const data = (res && res.data) || {};
  const details = data.details || {};
  let field = FORM_FIELDS.includes(details.field) ? details.field : null;
  let code = KNOWN_CODES.includes(data.code) ? data.code : null;
  if (data.code === "invalid_input" && INVALID_INPUT_CODES[field]) {
    code = INVALID_INPUT_CODES[field];
  }
  // The upload limits (413 / 415) show under the files.
  if (data.code === "upload_too_large") {
    field = "files";
    code = UPLOAD_REASONS.includes(details.reason)
      ? details.reason
      : "total_size";
  } else if (
    data.code === "file_too_large" ||
    data.code === "unsupported_type"
  ) {
    field = "files";
    code = data.code;
  }
  if (!code && !data.errorMsg) code = "generic";
  return { field: code ? field : null, code, text: data.errorMsg || "" };
}

// A 409 of the result write (revision_conflict, result_exists or stale_result): someone else
// changed the checklist, so the page shows the conflict banner and keeps the input.
export function isWriteConflict(err) {
  const res = err && err.response;
  return Boolean(res && res.status === 409);
}
